import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

// Paleta neutra-legal (não gritante)
const DEFAULT_COLORS = [
  "#2563eb", // azul
  "#16a34a", // verde
  "#f59e0b", // âmbar
  "#ef4444", // vermelho
  "#7c3aed", // roxo
  "#0891b2", // ciano
  "#d946ef", // magenta
];

type DataPoint = {
  label: string;
  [key: string]: number | string | null | undefined;
};

type SeriesConfig = {
  dataKey: string;
  name?: string;
  color?: string;
  isPercent?: boolean;
};

type ChartCardProps = {
  title: string;
  description?: string;
  data: DataPoint[];
  series: SeriesConfig[];
  stacked?: boolean;
  valueFormat?: (v: number | string | null | undefined) => string;
  tooltipExtra?: (label: string, payload: any[]) => React.ReactNode;
  yAxisDomain?: [number | 'auto' | 'dataMin' | 'dataMax', number | 'auto' | 'dataMin' | 'dataMax'];
  yAxisticks?: number[];
};

function defaultFormatter(v: any, isPercent?: boolean) {
  if (v == null) return "-";
  if (isPercent) return `${Number(v).toFixed(1)}%`;
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : String(v);
}

export function ChartCard({
  title,
  description,
  data,
  series,
  stacked = false,
  valueFormat,
  tooltipExtra,
  yAxisDomain,
  yAxisticks
}: ChartCardProps) {
  // Se não veio formatador, usa um default que respeita isPercent por série
  const fmtValue = (v: any, isPercent?: boolean) =>
    typeof valueFormat === "function" ? valueFormat(v) : defaultFormatter(v, isPercent);

  return (
    <div className="bg-white rounded-xl p-4 card-shadow h-[360px] flex flex-col">
      <div className="mb-2">
        <div className="text-sm font-semibold">{title}</div>
        {description ? <div className="text-xs text-slate-500 mt-1">{description}</div> : null}
      </div>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" />
            <YAxis
              domain={yAxisDomain ?? ['auto', 'auto']}
              ticks={Array.isArray(yAxisticks) ? yAxisticks : undefined}
              allowDecimals={false}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const rows = payload.map((p) => {
                  const s = series.find((s) => s.dataKey === p.dataKey);
                  const isPct = s?.isPercent;
                  return (
                    <div key={p.dataKey} className="flex items-center justify-between gap-6">
                      <span>{s?.name || p.dataKey}</span>
                      <span>{fmtValue(p.value, isPct)}</span>
                    </div>
                  );
                });
                const extra =
                  typeof tooltipExtra === "function" ? tooltipExtra(label as string, payload) : null;
                return (
                  <div className="bg-white/95 backdrop-blur rounded-lg shadow p-3 text-xs">
                    <div className="font-medium mb-1">
                      {title} · {label}
                    </div>
                    <div className="space-y-1">{rows}</div>
                    {extra ? <div className="mt-2 border-t pt-2">{extra}</div> : null}
                  </div>
                );
              }}
            />
            <Legend
              formatter={(value) => {
                const s = series.find((s) => s.dataKey === value);
                return s?.name || value;
              }}
            />
            {series.map((s, idx) => (
              <Bar
                key={s.dataKey}
                dataKey={s.dataKey}
                name={s.name}
                stackId={stacked ? "stack" : undefined}
                fill={s.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]}
                radius={[6, 6, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
